import React, { FunctionComponent, useCallback, useEffect, useRef } from 'react'
import styles from './TaskPage.module.sass'
import { useParams } from 'react-router'
import { HistoryFrame } from '../../../../responses/events'
import { useDerivedState, useNavigation, useRootDispatch, useRootSelector, useSocket } from '../../../../utils/hooks'
import { urlWithParams } from '../../../../http/utils'
import { CHANNELS, ROUTES } from '../../../../http/routes'
import { ChatBody, ChatEditor, ChatHeader } from '../../projects/ProjectPage/Chat/Chat'
import { SortDirection } from '../../../../constants/direction'
import { inactiveContext, statusContext } from '../../../../constants/contexts'
import { EMPTY_PROJECT_WORK_OBJECT } from '../../../../responses/objects'
import { objectSelector, projectSelector, taskSelector } from '../../../../utils/selectors'
import TaskGroup from '../../../etc/TaskGroup/TaskGroup'
import { push } from 'connected-react-router'
import ProjectInfo from '../../projects/ProjectPage/ProjectInfo/ProjectInfo'
import Helmet from 'react-helmet'
import { taskActions as dataTaskActions } from '../../../../redux/data/task/task-actions'
import { RequestStatus } from '../../../../responses/response'
import TopBarProgress from 'react-topbar-progress-indicator'
import { EMPTY_PROJECT } from '../../../../responses/projects'
import Participants from '../../projects/ProjectPage/Participants/Participants'
import { batch, shallowEqual } from 'react-redux'
import { ID } from '../../../../types/UtilityTypes'
import { TaskStatus } from '../../../../constants/enums'
import { historyActions } from '../../../../redux/global/history/history-actions'
import { isBriefEmpty } from '../../projects/ProjectPage/Brief/Brief'
import ObjectHeader from '../../projects/ProjectPage/ObjectHeader/ObjectHeader'
import TaskMobile from '../../projects/ProjectPage/tasks/TaskMobile/TaskMobile'
import { useQueries } from '../../../../utils/hooks/useQueries'
import { EMPTY_TASK } from '../../../../responses/tasks'
import { TaskEditState } from '../../../../redux/data/task/task-reducer'
import { isArchivedProject } from '../../projects/ProjectPage/ProjectPage'

const TaskPage: FunctionComponent = () => {
    const dispatch = useRootDispatch()
    const {taskId: id} = useParams<{ taskId: string }>()
    const [taskId, setTaskId] = useDerivedState<ID>(id)
    const task = useRootSelector(taskSelector(id), shallowEqual)
    const object = useRootSelector(objectSelector(task?.project_work_object_id)) ?? EMPTY_PROJECT_WORK_OBJECT
    const project = useRootSelector(projectSelector(object.project_id)) ?? EMPTY_PROJECT
    const status = useRootSelector(state => state.pages.task.status)

    const direction = useRootSelector(state => state.global.user.data?.settings.history_direction ?? SortDirection.DESC)
    const editState = useRootSelector(state => state.data.task.editState)

    useEffect(() => {
        if (TaskEditState.is.none(editState) && taskId == EMPTY_TASK.id) {
            setTaskId(id)
        }
    }, [editState, object.id, task?.id, task?.parent_id, setTaskId, id, taskId])
    const {isDesktop, isMobile} = useQueries()

    useEffect(() => {
        id && batch(() => {
            dispatch(dataTaskActions.get.request(id))
            dispatch(historyActions.fetch.request({
                type: 'task',
                taskId: id,
                filterType: ['comments', 'files']
            }))
        })
    }, [dispatch, id])

    useSocket<HistoryFrame>(urlWithParams({taskId: id})(CHANNELS.TASK), '.update', [id],
        frame => dispatch(historyActions.socketUpdate(frame.history))
    )

    const mainRef = useRef(null)
    useNavigation(mainRef)

    const taskChange = useCallback((taskId: ID) => {
        if (taskId !== 0) {
            dispatch(push(urlWithParams({taskId})(ROUTES.TASK)))
        }
        setTaskId(taskId)
    }, [dispatch, setTaskId])

    return <div className={styles.page}>
        {
            status === RequestStatus.FETCHING && <TopBarProgress/>
        }
        <Helmet>
            <title>{`${project.code} - ${task?.type.name}`}</title>
        </Helmet>
        <inactiveContext.Provider value={isArchivedProject(project.status_id)}>
            <statusContext.Provider value={status}>
                <div ref={mainRef} className={styles.main}>
                    <ProjectInfo minimal hideBrief={!isBriefEmpty(object.files, object.description)} project={project}/>
                    <ObjectHeader className={styles.object} withLink objectId={object.id}/>
                    {
                        task && (isMobile ? <TaskMobile
                            hideLink
                            parentId={task.parent_id || task.id}
                            taskId={taskId}
                            objectId={task.project_work_object_id}
                            projectId={project.id}
                            onChange={taskChange}
                        /> : <TaskGroup
                            hideLink
                            parentId={task.parent_id || task.id}
                            objectId={task.project_work_object_id}
                            projectId={project.id}
                            taskId={taskId}
                            onChange={taskChange}
                        />)
                    }
                    <ChatHeader/>
                    {
                        (direction === SortDirection.ASC && task?.status_id !== TaskStatus.CLOSED) &&
                        <ChatEditor
                            key={'editor'}
                            className={styles.topEditor}
                        />
                    }
                    <ChatBody className={styles.chat} scrollRef={mainRef}/>
                    {
                        (direction === SortDirection.DESC && task?.status_id !== TaskStatus.CLOSED) &&
                        <ChatEditor
                            key={'editor'}
                        />
                    }
                </div>
                {
                    isDesktop && <Participants project={project}/>
                }
            </statusContext.Provider>
        </inactiveContext.Provider>
    </div>
}

export default TaskPage